'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateAxleTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/axle-type/search');
};

export const useAxleTypeList = (page, rowsPerPage, searchKey, searchValue) => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).like(searchKey, searchValue, 'AND').setOrder(['name'], true);

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue]);
  const key = useMemo(() => ['/axle-type/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data,
    totalRecord: data?.totalRecord,
    error,
    isLoading
  };
};

export const getAxleTypeById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/axle-type/${id}${getTzParam()}`);
    return response?.data?.data ?? response?.data ?? null;
  } catch (error) {
    console.error('Failed to fetch axle type by ID:', error);
    throw error;
  }
};

export const getAxleTypeByName = async (name) => {
  if (!name) return null;
  try {
    const fb = new FilterBuilder().setPaging(0, 1).like('name', name, 'AND');
    const response = await fleetApi.post(`/axle-type/search${getTzParam()}`, fb.build());
    const data = response?.data?.data || response?.data || [];
    return data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Failed to fetch axle type by name:', error);
    return null;
  }
};

export const createAxleType = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/axle-type/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAxleTypeList();
    return response;
  } catch (error) {
    console.error('Error creating axle type:', error);
    throw error;
  }
};

export const updateAxleType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/axle-type/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAxleTypeList();
    return response;
  } catch (error) {
    console.error('Error updating axle type:', error);
    throw error;
  }
};

export const deleteAxleType = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/axle-type/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAxleTypeList();
    return response.data;
  } catch (error) {
    console.error('Error deleting axle type:', error);
    throw error;
  }
};
